import React from "react";
import { Redirect } from "react-router-dom/cjs/react-router-dom.min";

// —— 保留的模板基礎頁 ——
import Dashboard from "../pages/Dashboard/index";
import Calendar from "../pages/Calendar/index";

// Auth（改密碼/忘記密碼等，開發時參考/改接正式 API）
import AuthLogin from "../pages/Auth/login";
import Register from "../pages/Auth/Register";
import RecoverPassword from "../pages/Auth/RecoverPassword";
import ResetPassword from "../pages/Auth/ResetPassword";
import LockScreen from "../pages/Auth/LockScreen";
import Logout from "../pages/Auth/Logout";
import ConfirmMail from "../pages/Auth/ConfirmMail";
import EmailVerification from "../pages/Auth/EmailVerification";
import TwoStepVerification from "../pages/Auth/TwoStepVerification";

// Utility（錯誤/維護狀態頁 — DoD 需要的狀態頁基礎）
import StarterPage from "../pages/Utility/pages-starter";
import MaintenancePage from "../pages/Utility/pages-maintenance";
import ComingSoonPage from "../pages/Utility/pages-comingsoon";
import Error404 from "../pages/Utility/pages-404";
import Error500 from "../pages/Utility/pages-500";

// —— DC Keeper 模組頁：lazy 載入（code-split,首包不含各模組 + 圖表/xlsx 等重依賴）——
// 用 lazyWithRetry:chunk 因重編譯/重部署失效時自動重載一次,避免舊分頁整頁掛掉。
import { lazyWithRetry } from "../helpers/lazyWithRetry";
import { isDevNoteVisible } from "../components/DCKeeper/DevNote";
import { isModuleReleased } from "../helpers/releaseScope";

// 主登入
// 舊 /login(模板版登入頁)已退場:正式登入=/auth-login(Figma DCK-00 2128:21427,走 GetToken)
const LegacyLoginRedirect = () => <Redirect to="/auth-login" />;

const Accounts = lazyWithRetry(() => import("../pages/DCKeeper/Accounts"));
const AccountsUsers = lazyWithRetry(() => import("../pages/DCKeeper/Accounts").then((m) => ({ default: m.UsersRoute })));
const AccountsPerms = lazyWithRetry(() => import("../pages/DCKeeper/Accounts").then((m) => ({ default: m.PermsRoute })));
const AccountsWork = lazyWithRetry(() => import("../pages/DCKeeper/Accounts").then((m) => ({ default: m.WorkRoute })));
const DckDashboard = lazyWithRetry(() => import("../pages/DCKeeper/Dashboard"));
const Rooms = lazyWithRetry(() => import("../pages/DCKeeper/Rooms"));
const Registry = lazyWithRetry(() => import("../pages/DCKeeper/Registry"));
const RegistryIps = lazyWithRetry(() => import("../pages/DCKeeper/Registry").then((m) => ({ default: m.IpsPage })));
const RegistryProjects = lazyWithRetry(() => import("../pages/DCKeeper/Registry").then((m) => ({ default: m.ProjectsPage })));
const RegistryContracts = lazyWithRetry(() => import("../pages/DCKeeper/Registry").then((m) => ({ default: m.ContractsPage })));
const RegistryVendors = lazyWithRetry(() => import("../pages/DCKeeper/Registry").then((m) => ({ default: m.VendorsPage })));
const Assets = lazyWithRetry(() => import("../pages/DCKeeper/Assets"));
const AssetsCabinets = lazyWithRetry(() => import("../pages/DCKeeper/Assets").then((m) => ({ default: m.CabinetsRoute })));
const AssetsHardware = lazyWithRetry(() => import("../pages/DCKeeper/Assets").then((m) => ({ default: m.HardwareRoute })));
const AssetsSoftware = lazyWithRetry(() => import("../pages/DCKeeper/Assets").then((m) => ({ default: m.SoftwareRoute })));
const Monitor = lazyWithRetry(() => import("../pages/DCKeeper/Monitor"));
const MonitorWatch = lazyWithRetry(() => import("../pages/DCKeeper/Monitor").then((m) => ({ default: m.WatchPage })));
const MonitorDispatch = lazyWithRetry(() => import("../pages/DCKeeper/Monitor").then((m) => ({ default: m.DispatchPage })));
const Notify = lazyWithRetry(() => import("../pages/DCKeeper/Notify"));
const NotifySettings = lazyWithRetry(() => import("../pages/DCKeeper/Notify/NotifySettingsPage"));
const Reports = lazyWithRetry(() => import("../pages/DCKeeper/Reports"));
const Analysis = lazyWithRetry(() => import("../pages/DCKeeper/Analysis"));
const AnalysisAlerts = lazyWithRetry(() => import("../pages/DCKeeper/Analysis").then((m) => ({ default: m.AlertsPage })));
const AnalysisFinance = lazyWithRetry(() => import("../pages/DCKeeper/Analysis").then((m) => ({ default: m.FinancePage })));
const Approvals = lazyWithRetry(() => import("../pages/DCKeeper/Approvals"));
const AuditArchive = lazyWithRetry(() => import("../pages/DCKeeper/Approvals/AuditArchivePage"));
const Showcase = lazyWithRetry(() => import("../pages/DCKeeper/_Showcase"));
const Profile = lazyWithRetry(() => import("../pages/DCKeeper/Profile"));

const authProtectedRoutes = [
    // 智慧看板（模組02）— 首頁
    // 智慧看板也要守。原本以「它是登入後的落點,擋掉會沒地方可去」為由放行,
    // 但那個理由只在「導走」方案下成立 —— 我們選的是「顯示無權限畫面」(票 B2),
    // 停在原地不會沒地方可去。dashboard 在確認單裡是一個可被隱藏的模組,
    // 後端給 Dashboard: Hidden 時就該擋(8/13 code review)。
    { path: "/dashboard", component: DckDashboard, perm: "dashboard.read" },

    // 01 人員管理(側欄子項:權限設定/使用者資訊/工作管理;/dck/accounts 相容導向)
    { path: "/dck/accounts/users", component: AccountsUsers, perm: "user.read" },
    { path: "/dck/accounts/perms", component: AccountsPerms, perm: "role.read" },
    { path: "/dck/accounts/work", component: AccountsWork, perm: "work.read" },
    { path: "/dck/accounts", component: Accounts, perm: "user.read" },

    // 03 機房管理
    { path: "/dck/rooms", component: Rooms, perm: "room.read" },

    // 04 營運資訊(9 月交付,已對客戶開放):IP 已接 DcIpAddress、合約讀取側已接 DcContract;
    // 專案/廠商的後端仍 404,頁面暫以 mock 供稿(待確認清單 一、17)
    { path: "/dck/registry/ips", module: "registry", component: RegistryIps, perm: "ip.read" },
    // 專案／廠商:後端沒有 DcProject／DcVendor(頁面吃 mock 示範資料)→ 10/5 裁示部署站不開,只在 development
    { path: "/dck/registry/projects", module: "registry", devOnly: true, component: RegistryProjects, perm: "project.read" },
    { path: "/dck/registry/contracts", module: "registry", component: RegistryContracts, perm: "contract.read" },
    { path: "/dck/registry/vendors", module: "registry", devOnly: true, component: RegistryVendors, perm: "vendor.read" },
    { path: "/dck/registry", module: "registry", component: Registry, perm: "ip.read" },

    // 05 資產管理(機櫃/硬體/軟體;/dck/assets 相容導向含 ?tab=)
    { path: "/dck/assets/cabinets", component: AssetsCabinets, perm: "cabinet.read" },
    { path: "/dck/assets/hardware", component: AssetsHardware, perm: "hardware.read" },
    { path: "/dck/assets/software", component: AssetsSoftware, perm: "software.read" },
    { path: "/dck/assets", component: Assets, perm: "cabinet.read" },

    // 06 監控派工(監控/派工;選購空殼)
    { path: "/dck/monitor/watch", module: "monitor", component: MonitorWatch, perm: "monitor.read" },
    { path: "/dck/monitor/dispatch", devOnly: true, component: MonitorDispatch, perm: "monitor.read" },
    { path: "/dck/monitor", module: "monitor", component: Monitor, perm: "monitor.read" },

    // 07 訊息功能
    // 各類訊息設定要放在 /dck/notify 前面:路由是前綴比對,反過來會被查詢頁吃掉
    { path: "/dck/notify/settings", component: NotifySettings, perm: "notify.read" },
    { path: "/dck/notify", component: Notify, perm: "notify.read" },

    // 08 報表功能(空殼)
    { path: "/dck/reports", devOnly: true, component: Reports, perm: "report.read" },

    // 09 決策分析(告警預測/財務預測;選購空殼)
    { path: "/dck/analysis/alerts", devOnly: true, component: AnalysisAlerts, perm: "decision.read" },
    { path: "/dck/analysis/finance", devOnly: true, component: AnalysisFinance, perm: "decision.read" },
    { path: "/dck/analysis", devOnly: true, component: Analysis, perm: "decision.read" },

    // 共用:審核中心
    { path: "/dck/approvals/archive", module: "approvals", component: AuditArchive, perm: "approval.read" },
    { path: "/dck/approvals", module: "approvals", component: Approvals, perm: "approval.read" },

    // 個人資料(會員下拉)
    { path: "/profile", component: Profile },

    // P0 共用元件 showcase（dev 驗證用,不入側欄）
    // 元件展示與模板參考頁:全是假資料,部署站不掛(10/5 盤點時發現打網址進得去)
    { path: "/dev/components", devOnly: true, component: Showcase },

    // 模板參考頁（開發期保留，正式上線前可移除）
    { path: "/ref/dashboard", devOnly: true, component: Dashboard },
    { path: "/ref/calendar", devOnly: true, component: Calendar },

    // eslint-disable-next-line react/display-name
    { path: "/", exact: true, component: () => <Redirect to="/dashboard" /> },

    // catch-all 404（必須放最後）
    { path: "*", component: Error404 },
]
  // 尚未開放的模組(空殼/示意)只在 development 有路由;部署站連網址都進不去(2026-09-09 裁示)
  .filter((r) => !r.devOnly || isDevNoteVisible())
  // 還沒對客戶開放的模組:路由不掛,免得打網址進得去。**側欄吃同一張表**,
  // 不然會像 9/8~9/15 那樣側欄點得到、點進去 404(票 36)。
  .filter((r) => !r.module || isModuleReleased(r.module));

/**
 * 這個路徑現在有沒有掛路由 —— 側欄拿它決定畫不畫連結。
 *
 * 側欄不能只看交付範圍表:報表／決策／派工的路由是 `devOnly`(空殼頁,只有 development 有),
 * 根本不讀 `module`。只看表的話,把 `report` 打開就會再一次「側欄畫得出、點進去 404」。
 * 直接問路由表才是真的單一事實來源(票 36 review Standards #2)。
 */
const ROUTE_PATHS = new Set(authProtectedRoutes.map((r) => r.path));
export const hasRoute = (path) => ROUTE_PATHS.has(String(path || "").split("?")[0]);

const publicRoutes = [
    { path: "/login", component: LegacyLoginRedirect },

    // Utility
    { path: "/pages-maintenance", devOnly: true, component: MaintenancePage },
    { path: "/pages-comingsoon", devOnly: true, component: ComingSoonPage },
    { path: "/pages-starter", devOnly: true, component: StarterPage },
    { path: "/pages-404", component: Error404 },
    { path: "/pages-500", devOnly: true, component: Error500 },

    // Auth（參考/改接）
    { path: "/auth-login", component: AuthLogin },
    { path: "/auth-register", devOnly: true, component: Register },
    { path: "/auth-recoverpw", component: RecoverPassword },
    { path: "/auth-reset-password", component: ResetPassword },
    { path: "/auth-lock-screen", devOnly: true, component: LockScreen },
    { path: "/auth-logout", devOnly: true, component: Logout },
    { path: "/auth-confirm-mail", devOnly: true, component: ConfirmMail },
    { path: "/auth-email-verification", devOnly: true, component: EmailVerification },
    { path: "/auth-two-step-verification", devOnly: true, component: TwoStepVerification },
]
  // 模板殘留頁(註冊/鎖屏/驗證信/Coming soon/Starter/500…)產品沒用到、還掛著 Borex 字樣:只在 development 保留給對稿,
  // 部署站不給進(2026-09-09)。登入/忘記密碼/重設密碼/404 是產品在用的,留著。
  .filter((r) => !r.devOnly || isDevNoteVisible());

export { authProtectedRoutes, publicRoutes };
